"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("../util");
const { GET } = util_1.httpMethods;
/**
 * @classdesc Represents the class interfacing property related API-I endpoints
 * @class
 */
class Properties {
    constructor(api) {
        this.api = api;
    }
    /**
     * Get properties according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#properties-get-properties-get}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {?boolean} args.defunct - Optional. Set true to return all properties including those flagged as defunct (deleted)
     * @param {?number[]} args.propertyIds - Optional. The specific ids of the properties to be returned
     * @param {?string} args.responseFormat - Optional. Set to 'short to indicates that the short response format should be returned
     * @param {?boolean} args.status - Optional. Set true to return a list of missing/pending status information
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an array of data or
     * an error with the problem.
     */
    async getProperties(args) {
        const { defunct, propertyIds, responseFormat, status } = args || {};
        const methodName = 'getProperties';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'defunct', defunct, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'responseFormat', responseFormat, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'status', status, util_1.ValidationTypes.BOOLEAN),
        ];
        (0, util_1.validateParams)(promises);
        const route = 'properties';
        const input = {
            query: {
                defunct,
                propertyIds: propertyIds?.join(),
                responseFormat,
                status,
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
    /**
     * Get properties summary according to the parameters provided.
     *
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#properties-get-property-summary-get|API Call}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {!number[]} args.propertyIds - Required. The ids of the properties
     * @return {Promise} Assets - Returns a Promise that, when fulfilled, will either return an Array with the assets or
     * an Error with the problem.
     */
    async getPropertiesSummary(args) {
        const { propertyIds } = args || {};
        const methodName = 'getPropertiesSummary';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(promises);
        const route = `properties/${propertyIds.join()}/summary`;
        const input = {
            query: {
                propertyIds: propertyIds?.join(),
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
    /**
     * Get properties' pseudogateways according to the parameters provided.
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#properties-get-property-pseudogateways-get}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {!number[]} args.propertyIds - Required. The ids of the properties
     * @return {Promise} Assets - Returns a Promise that, when fulfilled, will either return an Array with the assets or
     * an Error with the problem.
     */
    async getPropertiesPseudogateways(args) {
        const { propertyIds } = args || {};
        const methodName = 'getPropertiesPseudogateways';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(promises);
        const route = `properties/${propertyIds.join()}/pseudogateways`;
        return this.api.send(GET, route);
    }
    /**
     * Get properties' pseudonodes according to the parameters provided.
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#properties-get-property-pseudonodes-get}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {!number[]} args.propertyIds - Required. The ids of the properties
     * @return {Promise} Assets - Returns a Promise that, when fulfilled, will either return an array with the assets or
     * an error with the problem.
     */
    async getPropertiesPseudonodes(args) {
        const { propertyIds } = args || {};
        const methodName = 'getPropertiesPseudonodes';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(promises);
        const route = `properties/${propertyIds.join()}/pseudonodes`;
        return this.api.send(GET, route);
    }
    /**
     * Get the properties gateways according to the parameters provided.
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#properties-get-property-gateways-with-full-data-get}
     * @param {Object} [args={}] - An object containing the arguments accepted by the API
     * @param {!number} args.propertyIds - Required. The ids of the properties
     * @param {?string} args.serviceIssues - Optional. set `include` to include node service issues for gateways
     * @param {?string} args.histories - Optional. set `include` to include histories for gateways
     * @param {?string} args.qcChecks - Optional. set `include` to include QC checks for gateways
     * @param {?string} args.sleepSchedule - Optional. set `include` to include sleep schedules for gateways modems
     * @return {Promise} Assets - Returns a Promise that, when fulfilled, will either return an Array with the assets or
     * an Error with the problem.
     */
    async getPropertiesGateways(args) {
        const { propertyIds, serviceIssues, histories, sleepSchedule, qcChecks } = args || {};
        const methodName = 'getPropertiesGateways';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'serviceIssues', serviceIssues, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'histories', histories, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'sleepSchedule', sleepSchedule, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'qcChecks', qcChecks, util_1.ValidationTypes.BOOLEAN),
        ];
        (0, util_1.validateParams)(promises);
        const route = `properties/${propertyIds.join()}/gateways`;
        const input = {
            query: {
                serviceIssues,
                sleepSchedule,
                histories,
                qcChecks,
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
    /**
     * Get properties' nodes according to the parameters provided.
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#properties-get-property-nodes-with-full-data-get}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {!number[]} args.propertyIds - Required. The specific ids of the properties to be returned
     * @param {?boolean} args.serviceIssues - Optional. set to true to include node service issues for nodes
     * @param {?boolean} args.histories - Optional. set to true to include histories for nodes
     * @param {?boolean} args.qcChecks - Optional. set to true to include QC checks for node
     * @param {?boolean} args.trapLatestGoodImage - Optional. set to true to include TRP last image hash
     * @param {?boolean} args.formulationBatch - Optional. set to true to include SCDs formulation batch
     * @return {Promise} Assets - Returns a Promise that, when fulfilled, will either return an Array with the assets or
     * an Error with the problem.
     */
    async getPropertiesNodes(args) {
        const { propertyIds, serviceIssues, histories, qcChecks, trapLatestGoodImage, formulationBatch } = args || {};
        const methodName = 'getPropertiesNodes';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'serviceIssues', serviceIssues, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'histories', histories, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'qcChecks', qcChecks, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'trapLatestGoodImage', trapLatestGoodImage, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'formulationBatch', formulationBatch, util_1.ValidationTypes.BOOLEAN),
        ];
        (0, util_1.validateParams)(promises);
        const route = `properties/${propertyIds.join()}/nodes`;
        const input = {
            query: {
                serviceIssues: serviceIssues ? 'include' : undefined,
                histories: histories ? 'include' : undefined,
                qcChecks: qcChecks ? 'include' : undefined,
                trapLatestGoodImage: trapLatestGoodImage ? 'include' : undefined,
                formulationBatch: formulationBatch ? 'include' : undefined,
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
    /**
     * Get properties' latest nodes logs according to the parameters provided.
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#properties-get-property-latest-node-logs-get}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {!number[]} args.propertyIds - Required. The specific ids of the properties to be returned
     * @param {?string} args.stampFormat - Optional. Custom format for timestamps (see https://momentjs.com/docs/#/displaying/format)
     * @return {Promise} Assets - Returns a Promise that, when fulfilled, will either return an Array with the assets or
     * an Error with the problem.
     */
    async getPropertiesNodeLogs(args) {
        const { propertyIds, stampFormat } = args || {};
        const methodName = 'getPropertiesNodeLogs';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'stampFormat', stampFormat, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(promises);
        const route = `properties/${propertyIds.join()}/nodes/logs`;
        const input = {
            query: {
                stampFormat,
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
    /**
     * Get properties' latest gateways logs according to the parameters provided.
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#properties-get-property-latest-gateway-logs-get}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {!number[]} args.propertyIds - Required. The specific ids of the properties to be returned
     * @param {?string} args.stampFormat - Optional. Custom format for timestamps (see https://momentjs.com/docs/#/displaying/format)
     * @return {Promise} Assets - Returns a Promise that, when fulfilled, will either return an Array with the assets or
     * an Error with the problem.
     */
    async getPropertiesGatewayLogs(args) {
        const { propertyIds, stampFormat } = args || {};
        const methodName = 'getPropertiesGatewayLogs';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'stampFormat', stampFormat, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(promises);
        const route = `properties/${propertyIds.join()}/gateways/logs`;
        const input = {
            query: {
                stampFormat,
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
}
exports.default = Properties;
//# sourceMappingURL=properties.js.map